import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import {
  CSeomatic,
  SeomaticData,
  CCenteredCopy,
  CCtaBoxFragments,
  CCtaBoxBlock,
  CCtaBox,
  CPartialTextMastheadFragments,
  CExpandableFaqs,
  CExpandableFaqsFragments,
  CExpandableFaqsEntry,
  CPartialTextMastheadBlock,
  CPartialTextMasthead,
  CFaqSlider,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')

  if (siteInfo.code === 'en-IN') {
    return {
      notFound: true,
    }
  }

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_faq_Entry {
            ctaBox {
              ...ctaBox
            }
            partialTextMasthead {
              ...partialTextMasthead
            }
          }
        }
        faqs: entries(section: "faq", site: $site) {
          ...expandableFaqsFaqEntry
        }
      }
      ${CCtaBoxFragments.field}
      ${CPartialTextMastheadFragments.field}
      ${CExpandableFaqsFragments.faqEntry}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      faqs: data.faqs,
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    ctaBox: Array<CCtaBoxBlock>
    partialTextMasthead: Array<CPartialTextMastheadBlock>
  }
  global: CraftGlobals
  faqs: Array<CExpandableFaqsEntry>
}

export default function Faq({ entry, faqs, global }: Props) {
  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CPartialTextMasthead field={entry.partialTextMasthead} />
      <CExpandableFaqs faqs={faqs} showSearchBar={true} />
      <CFaqSlider field={global.faqVideoSlider} />
      <CCtaBox field={entry.ctaBox} />
      <CCenteredCopy field={global.centeredCopy} />
    </DefaultLayout>
  )
}
